import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'

const BASE = '/v1/push'

/**
 * Whether this browser can take push at all.
 *
 * Three separate things, and they fail differently: Safari before 16.4 has no Push API,
 * an insecure origin has no service worker, and a user who denied permission cannot be
 * asked again from script. The screen says which one it is rather than showing a button
 * that does nothing.
 */
export function pushSupport(): { supported: boolean; reason: string | null } {
  if (typeof window === 'undefined') return { supported: false, reason: null }
  if (!('serviceWorker' in navigator)) {
    return {
      supported: false,
      reason: 'This browser has no service worker support, which web push needs. On an internal network, note that it also requires HTTPS.',
    }
  }
  if (!('PushManager' in window)) {
    return { supported: false, reason: 'This browser does not support the Push API.' }
  }
  if (typeof Notification === 'undefined') {
    return { supported: false, reason: 'This browser does not support notifications.' }
  }
  if (Notification.permission === 'denied') {
    return {
      supported: false,
      reason: 'Notifications are blocked for this site. Re-allow them in the browser’s site settings — a page cannot ask again once they are denied.',
    }
  }
  return { supported: true, reason: null }
}

/**
 * base64url, as the Push API wants the application server key.
 *
 * Returns an ArrayBuffer rather than a Uint8Array: TypeScript's DOM types require the key to
 * be backed by an ArrayBuffer specifically, and a Uint8Array is only assignable when its
 * buffer type is known not to be shared.
 */
function toKeyBytes(base64Url: string): ArrayBuffer {
  const padded = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    .padEnd(base64Url.length + ((4 - (base64Url.length % 4)) % 4), '=')
  const raw = atob(padded)
  const bytes = new Uint8Array(new ArrayBuffer(raw.length))
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i)
  return bytes.buffer
}

function toBase64Url(buffer: ArrayBuffer | null): string {
  if (!buffer) return ''
  const bytes = new Uint8Array(buffer)
  let binary = ''
  bytes.forEach((byte) => { binary += String.fromCharCode(byte) })
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/** The subscription this browser currently holds, if any. */
export function useCurrentSubscription() {
  return useQuery({
    queryKey: ['push', 'subscription'],
    queryFn: async () => {
      if (!pushSupport().supported) return null
      const registration = await navigator.serviceWorker.getRegistration('/sw.js')
      if (!registration) return null
      const subscription = await registration.pushManager.getSubscription()
      return subscription ? { endpoint: subscription.endpoint } : null
    },
    staleTime: 0,
  })
}

export function usePushMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['push'] })

  return {
    subscribe: useMutation({
      mutationFn: async () => {
        const permission = await Notification.requestPermission()
        if (permission !== 'granted') {
          throw new Error(
            permission === 'denied'
              ? 'You blocked notifications. Re-allow them in the browser’s site settings.'
              : 'Notifications were not allowed.',
          )
        }

        const registration = await navigator.serviceWorker.register('/sw.js')
        await navigator.serviceWorker.ready

        const { data } = await apiClient.get<{ publicKey: string }>(`${BASE}/public-key`)
        const subscription = await registration.pushManager.subscribe({
          // Required by every browser: a push that shows nothing to the user is not allowed.
          userVisibleOnly: true,
          applicationServerKey: toKeyBytes(data.publicKey),
        })

        await apiClient.post(`${BASE}/subscribe`, {
          endpoint: subscription.endpoint,
          p256dh: toBase64Url(subscription.getKey('p256dh')),
          auth: toBase64Url(subscription.getKey('auth')),
          userAgent: navigator.userAgent,
        })
        return { endpoint: subscription.endpoint }
      },
      onSuccess: invalidate,
    }),

    unsubscribe: useMutation({
      mutationFn: async () => {
        const registration = await navigator.serviceWorker.getRegistration('/sw.js')
        const subscription = await registration?.pushManager.getSubscription()
        if (!subscription) return

        // The server is told first. If the browser drops the subscription and the call then
        // fails, the server keeps sending to an endpoint nothing is listening on.
        await apiClient.post(`${BASE}/unsubscribe`, { endpoint: subscription.endpoint })
        await subscription.unsubscribe()
      },
      onSuccess: invalidate,
    }),
  }
}
